import React, {
    useEffect,
    useState,
    useContext,
    useCallback
} from 'react';
import appConfig from 'app-config';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { MLLineTypeUtil, convertToSentenceCase } from 'ml-portals-util-js';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';
import { UserService } from 'gw-capability-gateway';
import { MLWizardNavUtil } from 'ml-portals-util-react';
import { platformMessages} from 'ml-components-platform-react';
import PAPolicySummaryComponent from '../../../gw-capability-quoteandbind-pa-react/components/PAPolicySummaryComponent/PAPolicySummaryComponent';
import HOPolicySummaryComponent from '../../../gw-capability-quoteandbind-ho-react/components/HOPolicySummaryComponent/HOPolicySummaryComponent';
import PUPPolicySummaryComponent from '../../../gw-capability-quoteandbind-pup-react/components/PUPPolicySummaryComponent/PUPPolicySummaryComponent';
import PATrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PATrackEvent';
import HOTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/HOTrackEvent';
import PUMBTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PUMBTrackEvent';

import metadata from './SummaryPage.metadata.json5';
import styles from './SummaryPage.module.scss';
import messages from './SummaryPage.messages';
import { isNodeOrNull } from '@jutro/legacy/components/RadioField/utils';

const isGPA = appConfig.portalCode === 'GPA';

const SummaryPage = (props) => {
    const {
        wizardData: submissionVM, goNext, jumpTo, steps, isSkipping, changeNextSteps,updateWizardData
    } = props;
    const { portalCode } = appConfig;
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const [startPrint, updateStartPrint] = useState(false);
    const [policyDetails, updatePolicyDetails] = useState({});
    const [deviceValidationRequired, updateDeviceValidationRequired] = useState(false);
    const [uwIssues, setUwIssues] = useState();
    const quoteId = _.get(submissionVM, 'quoteID.value', undefined);
    const sessionUUID = _.get(submissionVM, 'sessionUUID.value', undefined);
    const { authHeader } = useAuthentication();
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const gpaMode = portalCode === 'GPA';
    const ampMode = portalCode === 'AMP';
    const qbMode = portalCode === 'QB';


    const PA_DRIVERS_STEP = '/drivers';
    const PA_VEHICLES_STEP = '/vehicles';
    const QUOTE_STEP = '/quote';
    const PAYMENT_STEP = '/payment-details';

    const {nextStepVisited} = MLWizardNavUtil(props);

    const goToStep = useCallback((stateName) => {
        const indexOfState = _.findIndex(
            steps,
            ({ path }) => path === stateName
        );
        jumpTo(indexOfState);
    }, [jumpTo, steps]);

    const onEditDriversClick = () => {
        goToStep(PA_DRIVERS_STEP);
    };

    const onEditVehiclesClick = () => {
        goToStep(PA_VEHICLES_STEP);
    };

    const onEditCoveragesClick = () => {
        goToStep(QUOTE_STEP);
    };

    const onChangePaymentMethodClick = () => {
        goToStep(PAYMENT_STEP);
    };

    const skipOnResumeIf = useCallback(() => {
        return !!_.get(submissionVM, 'bindData.policyNumber.value') || (gpaMode  && submissionVM.isBundle_ML.value && submissionVM.value.baseData.status_ML !== 'Draft' &&
        MLMultiLobsDataHolderService.isBeforeLastPurchaseFlowStepVisited('policySummary', submissionVM.value));
    }, [gpaMode, submissionVM]);

    const loadPageScript = async() => {
        if (gpaMode) {
            const scriptRequest = { quoteID: quoteId };
            const agentScript = await LoadSaveService.getScript('policySummary', scriptRequest, authHeader);
            submissionVM.script_ML.value = agentScript;
        }
        return submissionVM;
    };

     useEffect(() => {
        if (isGPA) {
            UserService.getCurrentUser(authHeader).then((user) => {
                if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
                    HOTrackEvent('Issuance Step Reached', false, submissionVM, user, { step: 'Summary Page' });
                }
                else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
                    PUMBTrackEvent('Issuance Step Reached', false, submissionVM, user, { step: 'Summary Page' });
                }
                else {
                    PATrackEvent('Issuance Step Reached', false, submissionVM, user, { step: 'Summary Page' });
                }
            });
        } else {
            if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
                HOTrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'Summary Page' }, true);
            }
            else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
                PUMBTrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'Summary Page' }, true);
            }
            else {
                PATrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'Summary Page' }, true);
            }
        }
     }, []);
    
    const syncPaymentMethod = () => {
        const paymentDetails = _.get(submissionVM, 'bindData.paymentDetails');
        const downPaymentMethod = _.get(paymentDetails, 'downPaymentMethod_ML.value');
        const instrument = _.get(paymentDetails, 'downPaymentInstrument_ML.value');
 
        if (downPaymentMethod === 'creditcard' && instrument) {
            _.set(paymentDetails, 'paymentMethod.value', 'creditcard');
            _.set(paymentDetails, 'selectedPaymentInstrument_ML.value', instrument);
            updateWizardData(submissionVM);
        }
    };

    const onNext = async(notCalledFromOnClick = true) => {
        const model = await loadPageScript();
        if (qbMode) {
            try {
                const hasUwIssues = await LoadSaveService.checkForBlockIssuanceUwIssues(quoteId);
                if (!hasUwIssues) {
                    if (!notCalledFromOnClick && !nextStepVisited()) {
                        return submissionVM;
                    }
                    syncPaymentMethod();
                    goNext();
                }
            } catch (error) {
                setUwIssues(error);
            }
        } else {
            if (_.isEmpty(model.script_ML.value)) {
                const agentSignatureOptionsIndex = _.findIndex(
                    steps, ({ path }) => path === '/agent-signature-options'
                );
                const nextSteps = _.slice(steps, agentSignatureOptionsIndex);
                changeNextSteps(nextSteps);
            }
            if (!notCalledFromOnClick && !nextStepVisited()) {
                return submissionVM;
            }
            goNext();
        }
         if (isGPA) {
            UserService.getCurrentUser(authHeader).then((user) => {
                if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
                    HOTrackEvent('Issuance Step Submitted', false, submissionVM, user, { step: 'Summary Page', action: 'Summary Page Submitted' });
                }
                else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
                    PUMBTrackEvent('Issuance Step Submitted', false, submissionVM, user, { step: 'Summary Page', action: 'Summary Page Submitted' });
                }
                else {
                    PATrackEvent('Issuance Step Submitted', false, submissionVM, user, { step: 'Summary Page', action: 'Summary Page Submitted' });
                }
            });
        } else {
            if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
                HOTrackEvent('Issuance Step Submitted', false, submissionVM, null, { step: 'Summary Page', action: 'Summary Page Submitted' }, true);
            }
            else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
                PUMBTrackEvent('Issuance Step Submitted', false, submissionVM, null, { step: 'Summary Page', action: 'Summary Page Submitted' }, true);
            }
            else {
                PATrackEvent('Issuance Step Submitted', false, submissionVM, null, { step: 'Summary Page', action: 'Summary Page Submitted' }, true);
            }
        }
        return false;
    };

    useEffect(() => {
        if (uwIssues) {
            throw uwIssues;
        }
    }, [uwIssues])

    useEffect(() => {
        const retrieveSubmissionDetails = () => {
            LoadSaveService.getSubmissionDetails({
                quoteID: quoteId,
                sessionUUID: sessionUUID
            }, authHeader).then((policy) => {
                const policyDetailsDTOML = viewModelService.create(
                    policy,
                    'pc',
                    'metlife.edge.capabilities.policy.dto.PolicyDetailsDTOML'
                );
                updatePolicyDetails(policyDetailsDTOML);
                updateDeviceValidationRequired(_.get(submissionVM, 'lobData.homeowners.modifiers_ML.isDeviceValidationRequired_ML.value', false));
            });
        };

        if (gpaMode) {
            if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
                onNext(true);
            } else if (_.isEmpty(submissionVM.script_ML.value)) {
                const agentSignatureOptionsIndex = _.findIndex(
                    steps, ({ path }) => path === '/agent-signature-options'
                );
                const nextSteps = _.slice(steps, agentSignatureOptionsIndex);
                changeNextSteps(nextSteps);
            }
            return;
        }

        if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
            retrieveSubmissionDetails();
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onPrintPolicySummary = () => {
        updateStartPrint(true);
    };

    const isPersonalAuto = (details) => {
        return _.get(details, 'lines.value', []).some((line) => MLLineTypeUtil.isPersonalAuto(line));
    };

    const isHomeOwners = (details) => {
        return _.get(details, 'lines.value', []).some((line) => MLLineTypeUtil.isHomeOwners(line));
    };

    const isPersonalUmbrella = (details) => {
        return _.get(details, 'lines.value', []).some((line) => MLLineTypeUtil.isPersonalUmbrella(line));
    };

    const getSummaryComponentOverrides = () => {
        let overrides = {};
        if (isPersonalAuto(policyDetails)) {
            overrides = {
                policySummaryComponent: {
                    value: policyDetails,
                    startPrint,
                    updateStartPrint,
                    editDrivers: onEditDriversClick,
                    editVehicles: onEditVehiclesClick,
                    editCoverages: onEditCoveragesClick,
                    editPayment: onChangePaymentMethodClick,
                    gpaMode,
                    qbMode
                },
            };
        } else if (isHomeOwners(policyDetails)) {
            overrides = {
                policySummaryComponent: {
                    value: policyDetails,
                    startPrint,
                    updateStartPrint,
                    editCoverages: onEditCoveragesClick,
                    editPayment: onChangePaymentMethodClick,
                    gpaMode,
                    qbMode
                }
            };
        } else if (isPersonalUmbrella(policyDetails)) {
            overrides = {
                policySummaryComponent: {
                    value: policyDetails,
                    startPrint,
                    updateStartPrint,
                    editDrivers: onEditDriversClick,
                    editCoverages: onEditCoveragesClick,
                    editPayment: onChangePaymentMethodClick,
                    gpaMode,
                    qbMode
                }
            };
        }

        return overrides;
    };

    const getSummaryContainerOverrides = () => {
        let overrides = {};
        if (gpaMode) {
            overrides = {
                summaryContainer: {
                    className: 'page-content-gpa',
                    visible: false
                }
            }
        } else {
            overrides = {
                summaryContainer: {
                    contentLayout: {
                        component: 'grid'
                    }
                }
            }

        }

        return overrides;
    }

    const getSummaryComponentResolver = () => {
        let resolver = {};
        if (isPersonalAuto(policyDetails)) {
            resolver = { policySummaryComponent: PAPolicySummaryComponent };
        }

        if (isHomeOwners(policyDetails)) {
            resolver = { policySummaryComponent: HOPolicySummaryComponent };
        }

        if (isPersonalUmbrella(policyDetails)) {
            resolver = { policySummaryComponent: PUPPolicySummaryComponent };
        }

        return resolver;
    };

    const getNextStepTextForGPA = () => {
        return (
            <FormattedMessage
                {...messages.continuTextforGPA}
                values={{
                    requiredSignatures: (
                        <strong>{translator(messages.requiredSignatures)}</strong>
                    )
                }}
            />
        );
    };

    const nextStepText_Ext = () => {
        return (
            <FormattedMessage
                {...messages.continueText}
                values={{
                    signPolicyDocuments: (
                        <strong>{translator(deviceValidationRequired ? messages.validateSmartHomeDevicesText : messages.signPolicyDocumentsText)}</strong>
                    )
                }}
            />
        );
    };

    const resolvers = {
        resolveComponentMap: {
            ...getSummaryComponentResolver()
        },
        resolveCallbackMap: {
            onNext: onNext,
            printPolicySummary: onPrintPolicySummary
        }
    };

    const overrides = {
        nextDescription: {
            visible: !gpaMode,
            content: nextStepText_Ext()
        },
        policySummaryComponent: {
            visible: false
        },
        pageHeader: {
            visible: !gpaMode,
            className: qbMode ? "page-header summaryPolicyInformation headerOne" : "page-header summaryPolicyInformation"
        },
        printSummaryButton: {
            className: qbMode ? "secondaryButton widthSmall" : "",
            content: qbMode ? convertToSentenceCase(translator(messages.printPolicySummary)) : translator(messages.printPolicySummary)
        },
        pageHeaderStatement: {
            visible: !gpaMode,
            content: qbMode ? messages.summaryPageHeaderStatementQB : messages.summaryPageHeaderStatement
        },
        stickySection: {
            visible: gpaMode
        },
        stickyfootercomponent: {
            nextDescription: getNextStepTextForGPA(),
            onNext
        },
        nextDescriptionforGPA: {
            visible: gpaMode,
            content: getNextStepTextForGPA()

        },

        ...getSummaryContainerOverrides(),
        ...getSummaryComponentOverrides()
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            skipWhen={skipOnResumeIf}
            showNext={!nextStepVisited()}
            onNext={onNext}
            nextLabel={translator(platformMessages.mlContinue)}
            showTextNextToButton={!nextStepVisited()}
            textNextToButton={!gpaMode ? nextStepText_Ext() : getNextStepTextForGPA()}
            metadataPageId={!nextStepVisited() && _.get(metadata, 'pageContent.id')}
            overrideDivId='summaryFooter'
            stickyComponentOverrideId='stickyfootercomponent'
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={policyDetails}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
};

SummaryPage.propTypes = wizardProps;
export default SummaryPage;
